import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/api/client";
import type { components } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Rate } from "@/features/think-tank/ThisWeek";
import { DESK_STATUS, errorMessage, orNull } from "@/features/think-tank/ttModel";

type TtDesk = components["schemas"]["TtDesk"];
type TtQuestion = components["schemas"]["TtQuestionPublic"];
type TtSource = components["schemas"]["TtSourcePublic"];
type QType = TtQuestion["qtype"];

const KEY = ["admin", "think-tank"] as const;

/** The Research desk: questions queued → researching → done, each a memo with its sources. */
export function Desk() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const query = useQuery({
    queryKey: [...KEY, "desk"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/questions");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const sources = useQuery({
    queryKey: [...KEY, "rules"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/think-tank/rules");
      if (error) throw new Error("failed to load");
      return data!;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: [...KEY] });
  const d = query.data;
  if (query.isLoading) return <p className="text-sm text-zinc-500">Loading…</p>;
  if (query.isError || !d) {
    return (
      <Card><CardBody>
        <p role="alert" className="text-sm text-red-700">Couldn’t load the Research desk. Your session may have expired.</p>
        <Button size="sm" variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button>
      </CardBody></Card>
    );
  }
  const q = d.questions.find(x => x.id === open);
  return (
    <div className="space-y-4">
      <Card data-testid="tt-desk"><CardBody>
        <h2 className="font-semibold">Questions</h2>
        <Ask d={d} onChanged={refresh} />
        <ul className="mt-3 divide-y divide-zinc-100">
          {d.questions.map(x => (
            <li key={x.id} className="py-2" data-testid={`tt-question-${x.id}`}>
              <button type="button" className="text-left text-sm font-medium hover:underline" onClick={() => setOpen(open === x.id ? null : x.id)}>{x.question}</button>
              {x.sample ? <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-500">Sample</span> : null}
              <span className={`block text-xs ${DESK_STATUS[x.status]}`}>{`${x.status_line}${x.recommendation ? ` · Recommendation: ${x.recommendation}` : ""}`}</span>
            </li>
          ))}
        </ul>
        {!d.questions.length ? <p className="py-2 text-sm text-zinc-500">No questions yet. Ask the first one above.</p> : null}
        <p className="mt-2 text-xs text-zinc-500">{d.ai_line}</p>
      </CardBody></Card>
      {q ? <Question key={q.id} q={q} d={d} sources={sources.data?.sources ?? []} onClose={() => setOpen(null)} onChanged={refresh} /> : null}
      <Card data-testid="tt-learn"><CardBody>
        <h2 className="font-semibold">Make the research better</h2>
        <p className="text-sm text-zinc-500">{d.learn_line}</p>
        {d.checklists.map(g => (
          <div key={g.qtype} className="mt-2">
            <h3 className="text-xs font-semibold uppercase text-zinc-500">{g.label}</h3>
            <ul className="list-disc pl-5 text-sm">{g.items.map(i => <li key={i}>{i}</li>)}</ul>
          </div>
        ))}
        {!d.checklists.length ? <p className="mt-2 text-sm text-zinc-500">No checklist items yet. Rate a memo or a report and say what was missing.</p> : null}
        <h3 className="mt-3 text-xs font-semibold uppercase text-zinc-500">Sources this month</h3>
        <ul className="text-sm">
          {(sources.data?.sources ?? []).filter(s => s.cited || s.trust !== "trusted").map(s => (
            <li key={s.id} data-testid={`tt-desk-source-${s.id}`}>{s.name} <span className="text-zinc-500">{s.line}</span></li>
          ))}
        </ul>
      </CardBody></Card>
    </div>
  );
}

function Ask({ d, onChanged }: { d: TtDesk; onChanged: () => void }) {
  const [question, setQuestion] = useState("");
  const [qtype, setQtype] = useState<QType>("market");
  const ask = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/questions", { body: { question: question.trim(), qtype } });
      if (error) throw new Error(errorMessage(error, "Couldn’t add the question."));
    },
    onSuccess: () => { setQuestion(""); onChanged(); },
  });
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto_auto]" role="group" aria-label="Ask a question">
      <input aria-label="Your question" placeholder="Ask a question" value={question} onChange={e => setQuestion(e.target.value)}
        className="h-9 rounded-md border border-zinc-300 px-2 text-sm" data-testid="tt-ask" />
      <select aria-label="Kind of question" value={qtype} onChange={e => setQtype(e.target.value as QType)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
        {d.qtypes.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Button size="sm" disabled={question.trim().length < 3 || ask.isPending} data-testid="tt-ask-save" onClick={() => ask.mutate()}>Ask</Button>
      {ask.isError ? <p role="alert" className="text-sm text-red-700 sm:col-span-3">{ask.error.message}</p> : null}
    </div>
  );
}

function Question({ q, d, sources, onClose, onChanged }: { q: TtQuestion; d: TtDesk; sources: TtSource[]; onClose: () => void; onChanged: () => void }) {
  const [memo, setMemo] = useState(q.memo ?? "");
  const [rec, setRec] = useState(q.recommendation ?? "");
  const patch = useMutation({
    mutationFn: async (body: { status?: TtQuestion["status"]; memo?: string | null; recommendation?: string | null }) => {
      const { error } = await api.PATCH("/api/v1/admin/think-tank/questions/{question_id}", { params: { path: { question_id: q.id } }, body });
      if (error) throw new Error(errorMessage(error, "Couldn’t save that."));
    },
    onSuccess: onChanged,
  });
  const draft = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST("/api/v1/admin/think-tank/questions/{question_id}/draft", { params: { path: { question_id: q.id } } });
      if (error) throw new Error(errorMessage(error, "Couldn’t draft it."));
      return data!;
    },
    // The draft goes into the editor, so Save memo never overwrites it (TT review fixes 2).
    onSuccess: (d: TtDesk) => {
      const drafted = d.questions.find(x => x.id === q.id);
      if (drafted) { setMemo(drafted.memo ?? ""); setRec(drafted.recommendation ?? ""); }
      onChanged();
    },
  });
  const useful = useMutation({
    mutationFn: async (v: { id: string; useful: boolean | null }) => {
      const { error } = await api.PATCH("/api/v1/admin/think-tank/question-sources/{item_id}", { params: { path: { item_id: v.id } }, body: { useful: v.useful } });
      if (error) throw new Error(errorMessage(error, "Couldn’t save that."));
    },
    onSuccess: onChanged,
  });
  return (
    <Card data-testid="tt-question"><CardBody>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">{q.question}</h2>
          <p className="text-sm text-zinc-500">{`${q.qtype_label} · ${q.status_line}${q.asked_by ? ` · asked by ${q.asked_by}` : ""}`}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
      {q.checklist.length ? (
        <div className="mt-2 rounded-md bg-zinc-50 p-2 text-sm" data-testid="tt-question-checklist">
          <p className="text-xs font-semibold uppercase text-zinc-500">This run’s checklist (from your ratings)</p>
          <ul className="list-disc pl-5">{q.checklist.map(i => <li key={i}>{i}</li>)}</ul>
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {q.status === "queued" ? <Button size="sm" disabled={patch.isPending} data-testid="tt-q-start" onClick={() => patch.mutate({ status: "researching" })}>Start researching</Button> : null}
        {q.status === "done" ? <Button size="sm" variant="secondary" disabled={patch.isPending} data-testid="tt-q-reopen" onClick={() => patch.mutate({ status: "researching" })}>Reopen</Button> : null}
        <Button size="sm" variant="secondary" disabled={draft.isPending || q.status === "done"} data-testid="tt-q-draft" onClick={() => draft.mutate()}>Draft the memo with AI</Button>
      </div>
      {draft.isError ? <p role="alert" className="mt-1 text-sm text-amber-800" data-testid="tt-q-draft-error">{draft.error.message}</p> : null}
      {!d.ai_ready ? <p className="mt-1 text-xs text-zinc-500">AI research runs when keys are connected. Write the memo by hand for now.</p> : null}
      <div className="mt-3 space-y-2">
        <textarea aria-label="The memo" placeholder="What you found, with its sources" value={memo} onChange={e => setMemo(e.target.value)}
          className="min-h-40 w-full rounded-md border border-zinc-300 p-2 text-sm" data-testid="tt-q-memo" />
        <input aria-label="Recommendation" placeholder="The recommendation in one line" value={rec} onChange={e => setRec(e.target.value)}
          className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" data-testid="tt-q-rec" />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" disabled={patch.isPending} data-testid="tt-q-save" onClick={() => patch.mutate({ memo: orNull(memo), recommendation: orNull(rec) })}>Save memo</Button>
          {q.status !== "done" ? (
            <Button size="sm" disabled={patch.isPending || !memo.trim()} data-testid="tt-q-done"
              onClick={() => patch.mutate({ memo: orNull(memo), recommendation: orNull(rec), status: "done" })}>Mark done</Button>
          ) : null}
        </div>
        {patch.isError ? <p role="alert" className="text-sm text-red-700">{patch.error.message}</p> : null}
        {q.written_by === "ai" ? <p className="text-xs text-zinc-500">Drafted by AI from the sources below; edit it like any memo.</p> : null}
      </div>
      <h3 className="mt-4 text-sm font-medium">{`Sources · ${q.sources.length}`}</h3>
      <ul className="divide-y divide-zinc-100">
        {q.sources.map(s => (
          <li key={s.id} className="flex flex-col gap-1 py-2 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid={`tt-qsource-${s.id}`}>
            <div className="min-w-0">
              {s.url ? <a className="font-medium underline" href={s.url} target="_blank" rel="noreferrer">{s.title}</a> : <span className="font-medium">{s.title}</span>}
              <span className="block text-xs text-zinc-500">{[s.source_name && `${s.source_name} (${s.source_trust})`, s.note].filter(Boolean).join(" · ")}</span>
            </div>
            <div className="flex gap-1" role="group" aria-label={`Was ${s.title} useful?`}>
              <Button size="sm" variant={s.useful === true ? "primary" : "secondary"} data-testid={`tt-useful-yes-${s.id}`} onClick={() => useful.mutate({ id: s.id, useful: s.useful === true ? null : true })}>Useful</Button>
              <Button size="sm" variant={s.useful === false ? "primary" : "secondary"} data-testid={`tt-useful-no-${s.id}`} onClick={() => useful.mutate({ id: s.id, useful: s.useful === false ? null : false })}>Not useful</Button>
            </div>
          </li>
        ))}
      </ul>
      <AddSource id={q.id} sources={sources} onChanged={onChanged} />
      {q.memo ? <Rate subject="memo" id={q.id} ratings={q.ratings} onChanged={onChanged} /> : null}
    </CardBody></Card>
  );
}

function AddSource({ id, sources, onChanged }: { id: string; sources: TtSource[]; onChanged: () => void }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [source, setSource] = useState("");
  const [note, setNote] = useState("");
  const add = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/api/v1/admin/think-tank/questions/{question_id}/sources", {
        params: { path: { question_id: id } }, body: { title: title.trim(), url: orNull(url), source_id: source || null, note: orNull(note) },
      });
      if (error) throw new Error(errorMessage(error, "Couldn’t add that source."));
    },
    onSuccess: () => { setTitle(""); setUrl(""); setNote(""); onChanged(); },
  });
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2" role="group" aria-label="Add a source to the memo">
      <input aria-label="Source title" placeholder="What it is" value={title} onChange={e => setTitle(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" data-testid="tt-qsource-title" />
      <input aria-label="Source link" placeholder="Link (https://…)" value={url} onChange={e => setUrl(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <select aria-label="Kind of source" value={source} onChange={e => setSource(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm">
        <option value="">Which source (optional)</option>
        {sources.map(s => <option key={s.id} value={s.id}>{s.name} · {s.trust}</option>)}
      </select>
      <input aria-label="What it says" placeholder="What it says (optional)" value={note} onChange={e => setNote(e.target.value)} className="h-9 rounded-md border border-zinc-300 px-2 text-sm" />
      <Button size="sm" variant="secondary" className="sm:w-fit" disabled={!title.trim() || add.isPending} data-testid="tt-qsource-add" onClick={() => add.mutate()}>Add source</Button>
      {add.isError ? <p role="alert" className="text-sm text-red-700 sm:col-span-2">{add.error.message}</p> : null}
    </div>
  );
}
